import {createSelector} from '@reduxjs/toolkit'

import {DateFormat, formatDateFns} from '@shared/helpers/date'
import {isEmpty} from '@shared/helpers/lang'

import {ROUTER_ORDER_SALE_HISTORY} from '@web/core/routes'
import {extractQueryFromUrl} from '@web/helpers/path'
import {commonSceneInfoSelector} from '@web/slices/common/slice'
import {locationSelector, pageQueryInfoSelector} from '@web/slices/router'

import {getDateMinMaxDate, getDateText} from '../utils'

/** Query 에서 Application 이 공통으로 이해할수 있는 값 전달 */
export const saleHistoryPageQueryInfoSelector = createSelector(pageQueryInfoSelector, ({query}) => {
    const {minDate, maxDate, isRange} = getDateMinMaxDate()

    const pageValid = !!Number(query.page) && Number(query.page) > 0
    const searchValid = ['advanced', 'filter'].includes(query.search)
    const keyword = query.keyword ? `${query.keyword}`.trim() : ''
    const {date, dateValid} = (function () {
        const start = query.startDate ?? ''
        const end = query.endDate ?? ''
        const startDate = start ? new Date(start) : null
        const endDate = end ? new Date(end) : null
        if (startDate && endDate && startDate <= endDate && isRange(startDate) && isRange(endDate)) {
            return {
                date: {
                    startDateText: getDateText(startDate),
                    endDateText: getDateText(endDate),
                    minDateText: getDateText(minDate),
                    maxDateText: getDateText(maxDate),
                },
                dateValid: true,
            }
        }
        return {
            date: {
                startDateText: undefined,
                endDateText: undefined,
                minDateText: undefined,
                maxDateText: undefined,
            },
            dateValid: false,
        }
    })()
    const errors: string[] = [
        {
            valid: query.page ? pageValid : true,
            code: 'PAGE',
        },
        {
            valid: query.startDate || query.endDate ? dateValid : true,
            code: 'DATE',
        },
        {valid: query.keyword ? !!keyword : true, code: 'SEARCH'},
        {valid: query.category ? !!query.category : true, code: 'STATUS'},
        {valid: query.search ? searchValid : true, code: 'FILTER'},
    ]
        .filter(({valid}) => !valid)
        .map(({code}) => code)

    return {
        page: pageValid ? query.page : undefined,
        date,
        category: query.category,
        keyword,
        search: searchValid ? query.search : undefined,
        valid: {
            isError: !isEmpty(errors),
            date: dateValid,
            replaceUrl: extractQueryFromUrl(window.location.href, [
                'page',
                'keyword',
                'category',
                'search',
                'startDate',
                'endDate',
            ]).cleanUrl,
        },
    }
})

/** 판매 > 현재 검색 상황 */
export const saleHistoryCurrentSearchSelector = createSelector(
    saleHistoryPageQueryInfoSelector,
    ({page, category, keyword, date, search}) => {
        return {
            page,
            keyword,
            search,
            category,
            startDate: date.startDateText,
            endDate: date.endDateText,
        }
    },
)

/** 판매 > 내역 > 검색 */
export const saleHistorySearchPageSelector = createSelector(
    saleHistoryCurrentSearchSelector,
    ({keyword, startDate, endDate, category}) => {
        return {
            initData: {
                keyword,
                startDate,
                endDate,
                categories: category ? [category] : [],
            },
        }
    },
)

/** 검색 키워드 노출 값 */
export const saleHistoryKeywordSelector = createSelector(saleHistoryCurrentSearchSelector, (currentSearch) => {
    return {
        keyword: currentSearch.keyword,
        dateText:
            currentSearch.startDate && currentSearch.endDate
                ? `${formatDateFns(currentSearch.startDate, DateFormat['yyyy. MM. dd.'])} ~ ${formatDateFns(
                      currentSearch.endDate,
                      DateFormat['yyyy. MM. dd.'],
                  )}`
                : null,
        category: currentSearch.category,
    }
})

/** 판매 > 노출 조건 */
export const saleHistoryPageRenderSelector = createSelector(
    saleHistoryPageQueryInfoSelector,
    commonSceneInfoSelector,
    ({page, search, keyword, category, valid}, {isRejected}) => {
        const shouldSearch = !page && (valid.date || keyword || category)
        const shouldSearchBox = search !== 'filter' && shouldSearch

        return {
            // 검색 api 호출 기준
            shouldSearch,
            // 판매 필터 노출
            shouldFilter: !shouldSearchBox && !isRejected,
            // search 스크롤 노출 조건
            shouldUpdateHeight: shouldSearchBox,
            /** 푸터 노출 여부 */
            shouldFooterRender: !isRejected,
        }
    },
)

export const saleHistoryAssignmentsPageQueryInfoSelector = createSelector(locationSelector, ({query}) => {
    return {
        returnUrl: query['rurl'] ?? ROUTER_ORDER_SALE_HISTORY.MAIN.getPath(),
        isPopup: query['action'] === 'POPUP',
        isDetail: query['entry'] === 'DETAIL',
    }
})
